import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { resolveDevScene } from '../../engine/dev/scenes.js';
import GameCanvas from '../GameCanvas.jsx';
import LoadingScreen from '../hud/LoadingScreen.jsx';
import TimeOfDay from '../hud/TimeOfDay.jsx';
import PerfBadge from '../hud/PerfBadge.jsx';
import HealthBar from '../hud/HealthBar.jsx';
import StaminaBar from '../hud/StaminaBar.jsx';
import AmmoBar from '../hud/AmmoBar.jsx';
import DebugPanel from '../hud/DebugPanel.jsx';
import Minimap from '../hud/Minimap.jsx';
import WantedStars from '../hud/WantedStars.jsx';
import CashBar from '../hud/CashBar.jsx';
import Wasted from '../hud/Wasted.jsx';
import PauseMenu from '../hud/PauseMenu.jsx';
import MissionPanel from '../hud/MissionPanel.jsx';
import Cutscene from '../hud/Cutscene.jsx';
import ShopPanel from '../hud/ShopPanel.jsx';
import PhonePanel from '../hud/PhonePanel.jsx';
import PhoneCall, { usePhoneCallActive } from '../hud/PhoneCall.jsx';
import { applyStoredAudioPrefs } from '../hud/AudioSettings.jsx';
import { PhoneButton, PhoneRing } from '../hud/PhoneHud.jsx';
import MissionArrow from '../hud/MissionArrow.jsx';
import RadioHud from '../hud/RadioHud.jsx';
import Prompt from '../hud/Prompt.jsx';
import FadeOverlay from '../hud/FadeOverlay.jsx';

/** Minimap edge, CSS px. The ammo and health slabs above it match this width. */
const MINIMAP_SIZE = 220;

/**
 * Forward-compat with wave C's `src/levels/index.js` (a `LEVELS` registry
 * keyed by level id). `import.meta.glob` on a literal path that doesn't
 * exist yet resolves to `{}` instead of failing `vite build` the way a bare
 * `import('../../levels/index.js')` would (Rollup resolves static import()
 * specifiers at bundle time, try/catch does not hide that) — this is the
 * same trick `engine/dev/scenes.js` already uses for its scene directory,
 * so once that file lands, real levels start resolving here with zero edits.
 */
const levelIndexGlob = import.meta.glob('../../levels/index.js');

async function resolvePlayScene(id) {
  const key = Object.keys(levelIndexGlob)[0];
  if (key) {
    try {
      const mod = await levelIndexGlob[key]();
      const LEVELS = mod.LEVELS ?? mod.default ?? null;
      const lvl = LEVELS?.[id];
      if (lvl) {
        return {
          buildLevel: lvl.buildLevel,
          createPost: lvl.createPost ?? null,
          engineOpts: lvl.engineOpts ?? {},
          params: { seed: id, id, ...lvl.params },
          label: lvl.name ?? id,
          gameplay: !!lvl.gameplay,
        };
      }
    } catch (err) {
      console.warn('[PlayRoute] levels/index.js failed to load, falling back to dev scenes', err);
    }
  }

  const scene = await resolveDevScene(id);
  return {
    buildLevel: scene.buildLevel,
    createPost: scene.createPost,
    engineOpts: scene.engineOpts,
    params: { seed: id, id },
    label: scene.label,
    // Dev scenes install their own gameplay when they want it.
    gameplay: false,
  };
}

/**
 * @file Hosts `GameCanvas` + every HUD overlay. The engine itself is never
 * React state (see GameCanvas's header comment) — `engineRef` holds the live
 * reference, and `engineTick` is a bare counter whose only job is to force a
 * re-render when `onEngine` fires, so the *ref's current value* gets read
 * fresh and handed to the HUD as a normal prop.
 */
export default function PlayRoute() {
  const { levelId } = useParams();
  const id = levelId ?? 'test';

  const engineRef = useRef(null);
  /** Whether this scene wants the player + enemy systems installed. */
  const wantsGameplayRef = useRef(false);
  const [, setEngineTick] = useState(0);
  const [status, setStatus] = useState('booting');
  const [statusDetail, setStatusDetail] = useState(null);
  // Gameplay install is synchronous and heavy (~0.4 s of car-archetype baking
  // on the city). The loading screen holds until it lands so the cost hides
  // under the fade instead of freezing the first playable frame.
  const [simReady, setSimReady] = useState(false);

  useEffect(() => {
    setSimReady(false);
  }, [id]);

  const resolve = useCallback(async () => {
    const entry = await resolvePlayScene(id);
    // Set before GameCanvas boots the engine, so the install effect below can
    // trust it once status flips to 'running'.
    wantsGameplayRef.current = entry.gameplay ?? false;
    return entry;
  }, [id]);

  const handleEngine = useCallback((engine) => {
    engineRef.current = engine;
    setEngineTick((t) => t + 1);
  }, []);

  const handleStatus = useCallback((next, detail) => {
    setStatus(next);
    setStatusDetail(detail ?? null);
  }, []);

  // Gameplay install: once the engine is running a registry level flagged
  // `gameplay: true`, spawn the player + enemy systems into it. Dev scenes
  // (gameplay: false) wire their own. Cleanup detaches + disposes the system
  // (Engine.dispose would too, but a same-engine re-run must not double-add).
  useEffect(() => {
    if (status !== 'running' || !wantsGameplayRef.current) return undefined;
    const engine = engineRef.current;
    if (!engine || !engine.level) return undefined;

    let cancelled = false;
    let gameplay = null;
    /** @type {any[]|null} Types this route built and therefore must free. */
    let ownedTypes = null;

    (async () => {
      const eng = await import('../../engine/index.js');
      /** Frees anything this run already built. Call before every early return. */
      const abandon = () => {
        if (ownedTypes) eng.disposeTypes(ownedTypes);
        ownedTypes = null;
      };
      if (cancelled || engine !== engineRef.current) return;

      const sp = engine.level.spawnPoints ?? {};
      let types = sp.enemyTypes;
      let spawnRules = sp.spawnRules;

      if (!types?.length) {
        // The authored roster. A level picks its cast with `enemyIds` (an array
        // of ids or a ROSTERS key); the spawn weights and caps ride along in the
        // defs, so nothing here knows what a gang member is.
        const roster = await import('../../game/enemies/index.js');
        const loaded = await roster.loadRoster(sp.enemyIds ?? 'city', {
          renderer: engine.renderer,
          caps: engine.caps,
          anisotropy: Math.min(4, engine.caps?.maxAnisotropy ?? 1),
        });
        if (cancelled || engine !== engineRef.current) {
          eng.disposeTypes(loaded.types);
          return;
        }
        if (loaded.types.length) {
          ownedTypes = loaded.types;
          types = loaded.types;
          spawnRules = spawnRules ?? loaded.spawnRules;
        } else {
          // Art missing entirely (fresh clone, bad path): the plain placeholder
          // keeps the level playable instead of showing an empty world.
          console.warn('[PlayRoute] no authored enemy art loaded — using the placeholder');
          ownedTypes = eng.buildBuiltinTypes({ seed: id });
          types = ownedTypes;
          spawnRules = spawnRules ?? [{ typeIdx: 0, weight: 1, maxAlive: 40, ringMin: 25, ringMax: 45 }];
        }
      }

      // Preload the chibi so the player never pops in a frame late.
      const sprite = await eng.loadPlayerSprite();
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      // THE FACTION TABLE. Game content (who the crews are, who hates whom,
      // which def id belongs to which side), so the ROUTE builds it and hands
      // it down — the engine never imports `src/game/`. A level that publishes
      // `map.districts` gets the population director and the whole gang/police
      // hostility model; anything else ignores the option entirely.
      const { createFactionRules } = await import('../../game/factions.js');
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      // THE MISSION SCRIPT. Game content again — the dialogue, the cutscene
      // art, which crew a job belongs to — so the ROUTE builds it and hands it
      // down beside the faction table, and the engine never imports
      // `src/game/`. A level that publishes `map.districts` gets the giver
      // rings, the objective machine and the escort; anything else ignores it.
      // The level may name its own set through `spawnPoints.missionIds`.
      const { createMissionScript } = await import('../../game/missions/index.js');
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      // THE LINE BANK. Game content for the third time — what a pedestrian
      // mutters, what a gang member shouts, which voice belongs to which side —
      // so the ROUTE builds it and hands it down beside the other two, and the
      // engine goes on never importing `src/game/`. Without this option no
      // speech director and no bubble batch are built at all.
      const { createDialogueScript } = await import('../../game/dialogue.js');
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      // THE WEAPON TABLE. Game content for the fourth time — what the gun is in
      // numbers (damage, cadence, clip, reload, how many rounds a box holds) —
      // so the ROUTE builds it and hands it down beside the other three, and the
      // engine goes on never importing `src/game/`. Without this option no
      // weapon system is built at all: the auto-attack keeps its old infinite
      // ammunition, no `'player:ammo'` is ever published and `AmmoBar` stays
      // `null`, which is exactly how every dev scene still runs.
      const { createWeaponTable } = await import('../../game/weapons.js');
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      // THE SOUND TABLE. Game content for the fifth time — which clips exist,
      // how loud each one is, how far it carries and what may steal it — so the
      // ROUTE builds it and hands it down beside the other four, and the engine
      // goes on never importing `src/game/`. Without this option no `Sfx`
      // facade and no `GameAudio` wiring are built at all: no `AudioContext` is
      // created, not one bus subscription is opened and the game is silent,
      // which is exactly how every dev scene still runs.
      const { createSoundTable } = await import('../../game/sfx.js');
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      // THE RADIO. Game content for the sixth time — which stations exist, what
      // they are called on air, which tracks they hold — so the ROUTE builds it
      // and hands it down beside the other five, and the engine goes on never
      // importing `src/game/`. Without this option no `Radio.js` is built at
      // all: nothing is ever on air, `'radio:track'` has no publisher, no die is
      // drawn from its private stream and `RadioHud` never draws, which is
      // exactly how every dev scene still runs.
      //
      // ⚠ `RADIO_TUNE` COMES WITH IT AS A SECOND OPTION, and it is not tidiness
      // that keeps it here: it is the MIX (how loud the radio sits under the
      // gunfire, how long it takes to arrive), it lives in `src/game/` beside
      // the stations it is about, and `GameAudio.js` is the thing that needs it.
      // The engine may not reach into this directory, so either the route passes
      // the numbers or the engine keeps a second copy of them — and a copied
      // pair that has to move together is the `links.js`/`countryroads.js` trap.
      const { createRadioTable, RADIO_TUNE } = await import('../../game/radio.js');
      if (cancelled || engine !== engineRef.current) {
        abandon();
        return;
      }

      gameplay = eng.createGameplay(engine, {
        types,
        spawnRules,
        level: engine.level,
        parent: engine.level.root,
        factions: createFactionRules(),
        missions: createMissionScript({ ids: sp.missionIds ?? null }),
        dialogue: createDialogueScript(),
        weapons: createWeaponTable(),
        sounds: createSoundTable(),
        radio: createRadioTable(),
        radioTune: RADIO_TUNE,
        ...(sprite ? { playerOpts: { texture: sprite.texture, aspect: sprite.aspect } } : {}),
      });
      engine.systems.push(gameplay);
      // ⚠ THE PLAYER'S SAVED VOLUMES APPLY HERE, NOT WHEN THE SETTINGS SCREEN
      // OPENS. `AudioSettings` pushes storage → mixer on mount, which is right
      // for the panel and useless for everybody else: without this line a
      // player who turned the sound down last session gets the shipped mix at
      // full level until they go and look at the panel again. A setting that
      // only applies once you open the screen that stores it is not a setting.
      // Silent no-op with no storage, no sound in the level, or a privacy mode
      // where `localStorage` throws.
      applyStoredAudioPrefs(engine);
      setSimReady(true);
    })().catch((err) => {
      console.error('[PlayRoute] gameplay install failed', err);
      // Never leave the loading screen holding on a failed install.
      setSimReady(true);
    });

    return () => {
      cancelled = true;
      if (gameplay) {
        const systems = engineRef.current?.systems;
        const at = systems ? systems.indexOf(gameplay) : -1;
        if (at >= 0) systems.splice(at, 1);
        gameplay.dispose();
        gameplay = null;
      }
      if (ownedTypes) {
        const doomed = ownedTypes;
        import('../../engine/index.js')
          .then((eng) => eng.disposeTypes(doomed))
          .catch(() => {});
        ownedTypes = null;
      }
    };
  }, [status, id]);

  // ── THE AUDIO UNLOCK ───────────────────────────────────────────────────────
  // ⚠ A BROWSER WILL NOT START AN `AudioContext` WITHOUT A USER GESTURE, and
  // `engine/audio/AudioBackend.js` creates one lazily on the first `unlock()`
  // precisely so nothing exists before that. So the route arms one listener and
  // spends it on the first press or tap anywhere in the play view.
  //
  // ⚠ IT STAYS ARMED UNTIL IT ACTUALLY REACHES A FACADE. The gameplay install
  // is async (roster, sprite and five content modules), so a player who clicks
  // the instant the canvas appears can beat it — and a "one-time" listener that
  // removed itself on that press would leave the game permanently silent with
  // no error. It resolves the system at gesture time, unlocks, and only then
  // stands down.
  //
  // CAPTURE + PASSIVE: capture so a HUD overlay that stops propagation cannot
  // swallow the one gesture we need, passive because we never preventDefault —
  // this must not cost the click it is riding on.
  useEffect(() => {
    if (status !== 'running') return undefined;
    let done = false;
    const opts = { capture: true, passive: true };
    function disarm() {
      window.removeEventListener('pointerdown', onGesture, opts);
      window.removeEventListener('keydown', onGesture, opts);
    }
    function onGesture(ev) {
      if (done || !ev?.isTrusted) return;
      const systems = engineRef.current?.systems;
      const gp = Array.isArray(systems) ? systems.find((s) => s?.name === 'gameplay') : null;
      const sfx = gp?.sfx ?? null;
      if (!sfx?.unlock) return; // not installed yet — wait for the next gesture
      done = true;
      Promise.resolve(sfx.unlock()).catch((err) => console.warn('[PlayRoute] audio unlock failed', err));
      disarm();
    }
    window.addEventListener('pointerdown', onGesture, opts);
    window.addEventListener('keydown', onGesture, opts);
    return disarm;
  }, [status, id]);

  const engine = engineRef.current;
  // ⚠ See `PhoneCall.jsx#usePhoneCallActive`: the call is the first overlay in
  // this game that is transparent on purpose, so it is the first one the HUD is
  // not simply hidden behind. The gameplay chrome stands down while somebody is
  // on the phone — by OPACITY, never by unmounting, because every piece of it
  // holds a live bus subscription that a remount would drop.
  const inCall = usePhoneCallActive(engine);

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0a0a0f]">
      <GameCanvas resolve={resolve} onEngine={handleEngine} onStatus={handleStatus} />

      {/* HUD layer: pointer-events-none wrapper so WASD/click-to-move reach
          the canvas underneath; each interactive piece opts back in.

          LAYOUT, so nothing collides:
            top-left     pause button (PauseMenu) + the dev status readout
            left/top-20  DebugPanel (?debug=1 only)
            top-centre   TimeOfDay, then RadioHud under it (top-14) — the
                         station ident, up for a few seconds when a track
                         starts. It is here because every other corner is a
                         reading taken MID-FIGHT or an ACTION to take, and a
                         station name is neither; the clock is the other thing
                         on this screen that simply states where you are. See
                         `RadioHud.jsx`'s header for the full argument.
            top-right    WantedStars, then CashBar under it (the menu link that
                         used to sit above them is DELETED — see below)
                         under that — the genre's own corner: heat, then money
            bottom-left  ammo slab, then health, then stamina, then the minimap —
                         ONE column, ONE width (`MINIMAP_SIZE`), which is why
                         none of the four positions itself. Ammo and health are
                         the two readings taken MID-FIGHT, with no time to look
                         for them: they belong in one place at one width, and
                         ammo sits above health because a reload is the thing you
                         act on while the health bar is still telling you not to.
                         ⚠ STAMINA SITS UNDER HEALTH, NOT OVER IT, and it is the
                         quietest slab in the column on purpose: it is a reading
                         you GLANCE at rather than one you make decisions on, so
                         it takes half the bar height, no numeral and no accent
                         colour until the player is actually out of breath. It
                         also AUTO-HIDES (opacity, never unmounting — the
                         PhoneCall rule) whenever it is full and nobody is
                         running, because a permanently-full bar is chrome noise.
                         ⚠ Its hidden box is RESERVED rather than collapsed, so
                         starting a sprint cannot slide the minimap and the
                         health slab up and down under the reading you are
                         taking. See `StaminaBar.jsx`'s header for both.
                         ⚠ AmmoBar is the only piece of this column that is
                         INTERACTIVE (two tap zones — reload, and the stance
                         pill; this game targets mobile). It opts back in with
                         `pointer-events-auto` on those two buttons ALONE, so a
                         drag over the numerals still reaches the canvas. ⚠ The
                         stamina strip deliberately adds NO third tap zone: a
                         sprint is a HELD state belonging to MOVEMENT, so its
                         touch affordance belongs with the movement input and not
                         on a readout.
                         All four return null until their channel speaks, and
                         the flex column closes the gap — a level with no weapon
                         table and no stamina model has this corner exactly as it
                         was.
            bottom-centre THREE lines, docked by geometry and never overlapping:
                         MissionArrow (bottom-40, the driving guide arrow, and
                         ONLY while driving), then PhoneRing (bottom-28, the
                         incoming-call banner), then Prompt (the "F — Enter"
                         line, bottom-16). All three can be up at once — a call
                         can arrive while you are driving toward a marker past a
                         doorway — so each clears the one under it by ~0.9 rem,
                         the same way the ring banner already cleared the prompt.
                         None of the three takes a z-index, deliberately: they
                         belong UNDER `FadeOverlay` so a door transition takes
                         them with the rest of the screen.
            bottom-right MissionPanel (bottom-14, over PerfBadge), then PerfBadge
            FULL SCREEN  FadeOverlay (z-30, always mounted at opacity 0), then
                         Wasted (z-40, only while dead), then PauseMenu (z-40+),
                         then Cutscene (z-60), then PhoneCall (z-62), then
                         ShopPanel (z-70), then PhonePanel (z-65 — see below)

          ⚠ THE PHONE ADDS TWO OVERLAYS AND ONE ARCHITECTURAL CHANGE. Five
          things can now freeze the loop, so NOBODY calls `loop.setTimeScale`
          any more: every overlay declares a named claim through
          `ui/hud/freezeOwner.js` and the world restarts only when the LAST
          claim goes. That closes `docs/shops.md`'s "the last one to restore
          wins" defect, which was already live with three overlays and would
          have been unarguable with five. The proof is a grep: there is no bare
          `setTimeScale` left anywhere under `src/ui` except inside that module.

          ⚠ PHONEPANEL RENDERS LAST BUT SITS AT z-65, UNDER THE TILL. That is
          not an oversight and the two facts are independent — DOM order only
          breaks ties between EQUAL z-indexes, and these are not equal. The till
          keeps z-70 and keeps ShopPanel's "innermost thing the player can be
          inside" argument intact, because a phone and a shop panel can never be
          open together: each modal's capture listener swallows every key, so P
          never reaches the sim while a shop is up and F never reaches it while
          the phone is. The ordering is therefore unobservable in play, and the
          conservative number is the one that leaves an existing file's
          documented claim true.

          ⚠ PHONECALL IS THE FIRST OVERLAY IN THIS GAME THAT IS TRANSPARENT ON
          PURPOSE. It has no `bg-void`: the world behind it stays fully visible
          (frozen, but drawn), which is the entire point of the VN treatment —
          you can see what you were doing while somebody talks to you. It sits
          just above Cutscene because it is the same kind of beat, and below the
          till for the reason above.
          PauseMenu renders late so its overlay is over everything — and
          Cutscene renders after it, because a briefing must be able to cover a
          pause menu that somehow got opened, never the other way round (it also
          swallows Esc; see its header).

          ⚠ SHOPPANEL IS LAST AND HIGHEST (z-70). A till is the INNERMOST thing
          the player can be inside: they walked through a door into a room 100 m
          under the city, then opened a menu at the counter. Anything drawn over
          it would be drawn over a screen the player is mid-transaction on, and
          the panel is also the innermost of the three overlays that FREEZE THE
          LOOP — so it must be the last one standing, for the same single-owner
          reason `Cutscene.jsx` documents: two frozen overlays, and the second
          one's cleanup restores the time scale to 1 while the first is still up.
          It swallows Esc rather than forwarding it, exactly like the cutscene.
          It returns null until `'shop:open'` arrives, so a level with no shops
          renders precisely as it did before this layer existed.

          ⚠ THE FADE IS THE LOWEST OF THE FULL-SCREEN LAYERS, on purpose: it
          blacks out the WORLD and the HUD over it (minimap, health, prompt —
          all z-auto), but a death splash, a pause menu or a briefing must be
          able to draw ON TOP of a fade that is still up. It is
          `pointer-events-none` in every state, so the pause button it covers at
          the same z-30 is still clickable through it. */}
      <div className="pointer-events-none absolute inset-0">
        <LoadingScreen engine={engine} status={status} holding={wantsGameplayRef.current && !simReady} />

        {/* ── THE GAMEPLAY CHROME ─────────────────────────────────────────────
            Everything the player reads WHILE PLAYING, in one group so it can
            step aside for a phone call. The full-screen overlays below are
            deliberately OUTSIDE it — a fade, a death splash, a pause menu, a
            briefing, a call and a till must never be dimmed by anything.
            `pointer-events-none` + `inset-0` so every child keeps positioning
            itself against the same box it always did. */}
        <div
          aria-hidden={inCall}
          className={`pointer-events-none absolute inset-0 transition-opacity duration-200 ${
            inCall ? 'opacity-0' : 'opacity-100'
          }`}
        >
        <TimeOfDay engine={engine} />
        {/* Under the clock, and inside the chrome block on purpose: a station
            ident is exactly the kind of thing that must step aside for a phone
            call, and it dims with the rest by OPACITY — it holds a live bus
            subscription of its own, so unmounting it would drop the very event
            it exists to catch. It returns null until `'radio:track'` arrives, so
            a level with no radio table has this slot exactly as it was. */}
        <RadioHud engine={engine} />
        <PerfBadge engine={engine} />
        <DebugPanel engine={engine} />
        <WantedStars engine={engine} />

        {/* The top-right money slot. It sits UNDER the wanted meter's own
            position (`right-3 top-14`, ~2.1 rem tall) rather than inside a
            shared flex column, because `WantedStars` positions itself and is
            not this wave's file to change — so the two are docked by geometry,
            not by a parent. `CashBar` returns null until the wallet speaks, and
            the meter is invisible until the first star, so on a level with
            neither this corner is empty exactly as it was. */}
        <div className="absolute right-3 top-[5.75rem] flex flex-col items-end gap-2">
          <CashBar engine={engine} />
        </div>

        <div className="absolute bottom-3 left-3 flex flex-col gap-2">
          <PhoneButton engine={engine} width={MINIMAP_SIZE} />
          <AmmoBar engine={engine} width={MINIMAP_SIZE} />
          <HealthBar engine={engine} width={MINIMAP_SIZE} />
          <StaminaBar engine={engine} width={MINIMAP_SIZE} />
          <Minimap engine={engine} size={MINIMAP_SIZE} />
        </div>

        <div className="absolute left-[3.25rem] top-3 select-none font-mono text-[10px] leading-4 text-hud-faint">
          <div>
            level: <span className="text-hud-dim">{id}</span>
            {statusDetail?.label ? ` (${statusDetail.label})` : ''}
          </div>
          <div>
            status: <span className="text-hud-dim">{status}</span>
            {statusDetail?.backend ? ` · ${statusDetail.backend}` : ''}
            {status === 'error' ? `: ${statusDetail?.message ?? statusDetail ?? 'unknown error'}` : ''}
          </div>
        </div>

        {/* ⚠ THE TOP-RIGHT "MENU" LINK IS DELETED (2026-08-14), not moved by
            accident. It sat one mis-tap away from the wanted meter and the cash
            readout, it was `pointer-events-auto` over live gameplay, and it went
            straight to `/` — which tears the level down and takes the wallet,
            the ammo pouch, the mission book and the wanted level with it. A
            one-click, unconfirmed, unrecoverable reset of a whole session is not
            a HUD control. Quitting lives in the pause menu now, where the player
            has already stopped playing to get there. */}

        <MissionPanel engine={engine} />
        <MissionArrow engine={engine} />
        <PhoneRing engine={engine} />
        <Prompt engine={engine} />
        </div>

        <FadeOverlay engine={engine} />
        <Wasted engine={engine} />
        <PauseMenu engine={engine} />
        <Cutscene engine={engine} />
        <PhoneCall engine={engine} />
        <ShopPanel engine={engine} />
        <PhonePanel engine={engine} />
      </div>
    </div>
  );
}
